/**
 * Market cap tiers. DB field `market_cap` is billions of USD ($B).
 */

export const MARKET_CAP_TIERS = [
  { id: "micro", label: "Micro Cap", rangeLabel: "Under $300 million" },
  { id: "small", label: "Small Cap", rangeLabel: "$300 million – $2 billion" },
  { id: "mid", label: "Mid Cap", rangeLabel: "$2 billion – $10 billion" },
  { id: "large", label: "Large Cap", rangeLabel: "$10 billion – $200 billion" },
  { id: "mega", label: "Mega Cap", rangeLabel: "Over $200 billion" },
];

export const VALID_MARKET_CAP_TIER_IDS = new Set(MARKET_CAP_TIERS.map((t) => t.id));

/** Max plausible market cap in $B (sanity check on input). */
const MAX_MARKET_CAP_BILLIONS = 5_000;

/** @param {string|null|undefined} tierId */
export function isValidMarketCapTierId(tierId) {
  return tierId != null && VALID_MARKET_CAP_TIER_IDS.has(tierId);
}

/** @param {string|null|undefined} tierId */
export function getMarketCapTierMeta(tierId) {
  if (!isValidMarketCapTierId(tierId)) return null;
  return MARKET_CAP_TIERS.find((t) => t.id === tierId) || null;
}

/**
 * @param {number|string|null|undefined} stored
 * @returns {number|null} billions ($B)
 */
export function getStockMarketCapBillions(stored) {
  const n = Number(stored);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * @param {number|string|null|undefined} stored
 * @returns {string|null}
 */
export function getMarketCapTierId(stored) {
  const billions = getStockMarketCapBillions(stored);
  if (billions == null || billions === 0) return null;

  if (billions < 0.3) return "micro";
  if (billions < 2) return "small";
  if (billions < 10) return "mid";
  if (billions < 200) return "large";
  return "mega";
}

/**
 * Validate CSV / form input as billions ($B).
 * @param {number|string|null|undefined} raw
 * @returns {{ ok: true, value: number|undefined } | { ok: false, error: string }}
 */
export function parseMarketCapBillions(raw) {
  if (raw == null || raw === "") return { ok: true, value: undefined };
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return { ok: false, error: "Market Cap must be a finite number" };
  }
  if (n < 0) {
    return { ok: false, error: "Market Cap cannot be negative" };
  }
  if (n > MAX_MARKET_CAP_BILLIONS) {
    return { ok: false, error: "Market Cap is out of expected range" };
  }
  return { ok: true, value: n };
}

/**
 * @param {Array<{ market_cap?: number|string|null, equity?: number|string|null }>} stocks
 */
export function buildMarketCapTierBreakdown(stocks) {
  const counts = Object.fromEntries(MARKET_CAP_TIERS.map((t) => [t.id, 0]));
  const investments = Object.fromEntries(MARKET_CAP_TIERS.map((t) => [t.id, 0]));
  let missing = 0;

  for (const stock of stocks || []) {
    const tierId = getMarketCapTierId(stock?.market_cap);
    if (!tierId) {
      missing += 1;
      continue;
    }
    counts[tierId] += 1;
    const equity = Number(stock?.equity);
    if (Number.isFinite(equity)) investments[tierId] += equity;
  }

  const total = MARKET_CAP_TIERS.reduce((sum, t) => sum + counts[t.id], 0);
  const totalInvestment = MARKET_CAP_TIERS.reduce((sum, t) => sum + investments[t.id], 0);

  return {
    missing,
    total,
    totalInvestment,
    segments: MARKET_CAP_TIERS.map((t) => ({
      id: t.id,
      label: t.label,
      rangeLabel: t.rangeLabel,
      count: counts[t.id],
      pct: total > 0 ? (counts[t.id] / total) * 100 : 0,
      investment: investments[t.id],
      investmentPct: totalInvestment > 0 ? (investments[t.id] / totalInvestment) * 100 : 0,
    })),
  };
}
